import React, { useEffect, useMemo, useState } from "react";
import {
  deleteSocialInsuranceAttachment,
  downloadSocialInsuranceAttachment,
  listSocialInsurance,
  saveSocialInsurance,
  uploadSocialInsuranceAttachment,
} from "../../api/socialInsurance";
import { validateEmployeeDocumentFile } from "../../api/employeeDocuments";
import { isOwnerRole } from "../../utils/hrStaffHelpers";
import {
  ATTACHMENT_KINDS,
  BLANK_FORMS,
  EMPLOYMENT_TYPES,
  GENDER_OPTIONS,
  INSURANCE_STATUSES,
  MARITAL_OPTIONS,
  assessInsuranceFile,
  buildDraftFromEmployee,
  deadlineLabel,
  describeDeadline,
  emptyPeriod,
} from "../../utils/socialInsurance";
import SocialInsurancePrint from "./SocialInsurancePrint";
import "./HrSocialInsurance.css";

const TABS = [
  ["form1", "نموذج 1"],
  ["form3", "نموذج 3"],
  ["form4", "نموذج 4"],
  ["form5", "نموذج 5"],
  ["files", "المرفقات"],
];

function messageOf(error, fallback) {
  const message = error?.response?.data?.message || error?.message;
  if (Array.isArray(message)) return message.join("\n");
  return message || fallback;
}

function draftFromItem(item, officeDefaults) {
  if (!item?.insuranceCase) {
    const employee = item?.employee || {};
    return buildDraftFromEmployee(
      {
        name: employee.fullName,
        legalFullName: employee.legalFullName,
        nationalId: employee.nationalId,
        birthDate: employee.birthDate,
        gender: employee.gender,
        phone: employee.phone,
        address: employee.address,
        maritalStatus: employee.maritalStatus,
        educationLevel: employee.educationLevel,
        jobTitle: employee.jobTitle,
        joinDate: employee.hireDate,
        salary: employee.salary,
        socialInsuranceNumber: employee.socialInsuranceNumber,
      },
      officeDefaults,
    );
  }
  const row = item.insuranceCase;
  return {
    status: row.status,
    insuranceNumber: row.insuranceNumber || "",
    submissionNotes: row.submissionNotes || "",
    form1: { ...(row.form1 || {}) },
    form3: { periods: [], ...(row.form3 || {}), periods: row.form3?.periods || [] },
    form4: { ...(row.form4 || {}) },
    form5: { ...(row.form5 || {}) },
    attachments: row.attachments || [],
  };
}

function Field({ label, children, wide = false }) {
  return (
    <label className={`si-field${wide ? " wide" : ""}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

export default function HrSocialInsurance({ userRole }) {
  const readOnly = isOwnerRole(userRole);
  const [items, setItems] = useState([]);
  const [officeDefaults, setOfficeDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("open");
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("form1");
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await listSocialInsurance();
      setItems(Array.isArray(data?.items) ? data.items : []);
      setOfficeDefaults(data?.officeDefaults || {});
    } catch (err) {
      setError(messageOf(err, "تعذر جلب ملفات التأمينات"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const selected = items.find((item) => item.employee.id === selectedId) || null;

  const rows = useMemo(() => {
    const term = query.trim();
    return items.filter((item) => {
      const status = item.insuranceCase?.status || "";
      const tone = item.deadline?.tone;
      const started = Boolean(item.insuranceCase);
      if (filter === "open" && (status === "SUBMITTED" || status === "REGISTERED")) {
        return false;
      }
      if (filter === "overdue" && tone !== "overdue") return false;
      if (filter === "soon" && tone !== "soon") return false;
      if (filter === "submitted" && status !== "SUBMITTED" && status !== "REGISTERED") {
        return false;
      }
      if (filter === "missing" && started && status !== "DRAFT") return false;
      if (!term) return true;
      const haystack = `${item.employee.fullName} ${item.employee.employeeCode} ${item.employee.jobTitle || ""}`;
      return haystack.includes(term);
    });
  }, [items, query, filter]);

  const summary = useMemo(() => {
    const open = items.filter(
      (item) =>
        item.insuranceCase?.status !== "SUBMITTED" &&
        item.insuranceCase?.status !== "REGISTERED",
    );
    return {
      total: items.length,
      overdue: open.filter((item) => item.deadline?.tone === "overdue").length,
      soon: open.filter((item) => item.deadline?.tone === "soon").length,
      submitted: items.filter((item) =>
        ["SUBMITTED", "REGISTERED"].includes(item.insuranceCase?.status),
      ).length,
    };
  }, [items]);

  function openEmployee(item) {
    setSelectedId(item.employee.id);
    setDraft(draftFromItem(item, officeDefaults));
    setTab("form1");
    setError("");
    setSuccess("");
  }

  function patch(section, values) {
    setDraft((prev) => ({ ...prev, [section]: { ...prev[section], ...values } }));
  }

  const review = draft
    ? assessInsuranceFile({
        ...draft,
        jobTitle: selected?.employee?.jobTitle,
      })
    : { missing: [], complete: false };

  async function persist(status) {
    if (!selected || !draft || readOnly) return;
    if (status && status !== "DRAFT" && status !== draft.status) {
      const check = assessInsuranceFile({
        ...draft,
        jobTitle: selected.employee.jobTitle,
      });
      if (!check.complete) {
        setError(check.missing.join("\n"));
        return;
      }
    }
    if (status === "REGISTERED" && !String(draft.insuranceNumber || "").trim()) {
      setError("أدخل رقم التأمين بعد التسجيل في المكتب");
      return;
    }
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const saved = await saveSocialInsurance(selected.employee.id, {
        status: status || draft.status || "DRAFT",
        form1: {
          ...draft.form1,
          hasPreviousPeriods: Boolean(draft.form1.hasPreviousPeriods),
        },
        form3: draft.form3,
        form4: {
          ...draft.form4,
          hasPreviousPeriods: Boolean(draft.form1.hasPreviousPeriods),
        },
        form5: draft.form5,
        insuranceNumber: draft.insuranceNumber,
        submissionNotes: draft.submissionNotes,
      });
      setSuccess("تم حفظ ملف التأمينات");
      await load();
      setDraft((prev) => ({
        ...prev,
        status: saved.status,
        attachments: saved.attachments || prev.attachments,
        insuranceNumber: saved.insuranceNumber || prev.insuranceNumber,
      }));
    } catch (err) {
      setError(messageOf(err, "تعذر حفظ الملف"));
    } finally {
      setBusy(false);
    }
  }

  async function onUpload(kind, file) {
    if (!file || !selected || readOnly) return;
    const invalid = validateEmployeeDocumentFile(file);
    if (invalid) {
      setError(invalid);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const saved = await uploadSocialInsuranceAttachment(selected.employee.id, {
        file,
        kind,
      });
      setDraft((prev) => ({
        ...prev,
        attachments: [saved, ...(prev.attachments || [])],
      }));
      await load();
    } catch (err) {
      setError(messageOf(err, "تعذر رفع المرفق"));
    } finally {
      setBusy(false);
    }
  }

  async function onDeleteAttachment(id) {
    if (readOnly) return;
    setBusy(true);
    try {
      await deleteSocialInsuranceAttachment(id);
      setDraft((prev) => ({
        ...prev,
        attachments: (prev.attachments || []).filter((file) => file.id !== id),
      }));
      await load();
    } catch (err) {
      setError(messageOf(err, "تعذر حذف المرفق"));
    } finally {
      setBusy(false);
    }
  }

  function printFile() {
    document.body.classList.add("si-printing");
    const cleanup = () => document.body.classList.remove("si-printing");
    window.addEventListener("afterprint", cleanup, { once: true });
    window.print();
  }

  const locked = readOnly || draft?.status === "REGISTERED";
  const deadlineInfo = draft
    ? describeDeadline(draft.form1.hireDate || selected?.employee?.hireDate, draft.status)
    : null;

  return (
    <div className="si-page" dir="rtl">
      <div className="ess-home-header">
        <h1 className="ess-home-title">التأمينات الاجتماعية</h1>
        <p className="ess-home-subtitle">
          طلب اشتراك المؤمن عليه (نموذج س1) يُقدَّم لمكتب التأمينات خلال أسبوعين
          من تاريخ الالتحاق، من أصل وصورتين مع المرفقات.
        </p>
      </div>

      <details className="si-rules" open={!selected}>
        <summary>شروط التقديم والمرفقات</summary>
        <ol>
          <li>يحرر صاحب العمل أو المفوض عنه نموذج رقم 1 (س1) عند التحاق العامل.</li>
          <li>يُسلَّم لمكتب التأمينات الذي يقع نشاط المنشأة في دائرته خلال أسبوعين.</li>
          <li>يُرفق مستند تاريخ الميلاد المميكن، والمؤهل، وبطاقة الرقم القومي.</li>
          <li>للمهندس والطبيب والمحامي والمحاسب ومن في حكمهم: ما يفيد الاشتراك بالنقابة.</li>
          <li>قرار التعيين أو بيان بدء الاشتراك أو عقد العمل، وإقرار استلام العمل إن وجد.</li>
          <li>نموذج 3 عند وجود مدد سابقة، ونموذج 4 لبيان المدد أو معاش آخر.</li>
          <li>تقرير اللياقة الطبية نموذج 111، ونموذج 5 لبيانات التغطية.</li>
          <li>تُراجع الأوراق في قسم المعلومات بمكتب التأمينات.</li>
        </ol>
        <p>
          {BLANK_FORMS.map((form) => (
            <a key={form.href} href={form.href} target="_blank" rel="noreferrer">
              {form.label}
              <br />
            </a>
          ))}
        </p>
      </details>

      {error ? <div className="si-error">{error}</div> : null}
      {success ? <div className="si-ok">{success}</div> : null}

      {selected && draft ? (
        <section className="si-editor">
          <div className="si-editor-head">
            <div>
              <button type="button" className="si-back" onClick={() => setSelectedId(null)}>
                رجوع للقائمة
              </button>
              <h2>
                {selected.employee.fullName}{" "}
                <span className="si-muted">{selected.employee.employeeCode}</span>
              </h2>
              <p className={`si-tone-${deadlineInfo?.tone || "ok"}`}>
                {INSURANCE_STATUSES[draft.status] || "لم يبدأ"} — {deadlineLabel(deadlineInfo)}
                {deadlineInfo?.deadlineDate ? ` (حتى ${deadlineInfo.deadlineDate})` : ""}
              </p>
              <p className="si-muted">
                الطباعة نسخة معبأة. النماذج الرسمية الفارغة في روابط الشروط أعلاه إذا طلب المكتب الورقة الأصلية.
              </p>
            </div>
            <div className="si-actions">
              <button type="button" onClick={printFile}>طباعة النماذج المعبأة</button>
              {!readOnly && draft.status !== "REGISTERED" ? (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => persist(draft.status || "DRAFT")}
                  >
                    حفظ
                  </button>
                  {draft.status === "SUBMITTED" ? (
                    <button type="button" disabled={busy} onClick={() => persist("DRAFT")}>
                      إرجاع للمسودة
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="is-primary"
                      disabled={busy || !review.complete}
                      onClick={() => persist("SUBMITTED")}
                    >
                      تم التسليم للمكتب
                    </button>
                  )}
                </>
              ) : null}
            </div>
          </div>

          <div className="si-tabs">
            {TABS.map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={tab === id ? "is-on" : ""}
                onClick={() => setTab(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === "form1" ? (
            <fieldset disabled={locked} className="si-grid">
              <Field label="اسم المنشأة">
                <input value={draft.form1.establishmentName || ""} onChange={(e) => patch("form1", { establishmentName: e.target.value })} />
              </Field>
              <Field label="رقم المنشأة">
                <input value={draft.form1.establishmentNumber || ""} onChange={(e) => patch("form1", { establishmentNumber: e.target.value })} />
              </Field>
              <Field label="عنوان المنشأة" wide>
                <input value={draft.form1.establishmentAddress || ""} onChange={(e) => patch("form1", { establishmentAddress: e.target.value })} />
              </Field>
              <Field label="المحافظة">
                <input value={draft.form1.governorate || ""} onChange={(e) => patch("form1", { governorate: e.target.value })} />
              </Field>
              <Field label="النشاط">
                <input value={draft.form1.activity || ""} onChange={(e) => patch("form1", { activity: e.target.value })} />
              </Field>
              <Field label="مكتب التأمينات" wide>
                <input value={draft.form1.insuranceOffice || ""} onChange={(e) => patch("form1", { insuranceOffice: e.target.value })} />
              </Field>
              <Field label="اسم المؤمن عليه">
                <input value={draft.form1.fullName || ""} onChange={(e) => patch("form1", { fullName: e.target.value })} />
              </Field>
              <Field label="الرقم القومي">
                <input value={draft.form1.nationalId || ""} inputMode="numeric" maxLength={14} onChange={(e) => patch("form1", { nationalId: e.target.value })} />
              </Field>
              <Field label="تاريخ الميلاد">
                <input type="date" value={String(draft.form1.birthDate || "").slice(0, 10)} onChange={(e) => patch("form1", { birthDate: e.target.value })} />
              </Field>
              <Field label="محل الميلاد">
                <input value={draft.form1.birthPlace || ""} onChange={(e) => patch("form1", { birthPlace: e.target.value })} />
              </Field>
              <Field label="النوع">
                <select value={draft.form1.gender || ""} onChange={(e) => patch("form1", { gender: e.target.value })}>
                  <option value="">اختر</option>
                  {GENDER_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="الجنسية">
                <input value={draft.form1.nationality || ""} onChange={(e) => patch("form1", { nationality: e.target.value })} />
              </Field>
              <Field label="الحالة الاجتماعية">
                <select value={draft.form1.maritalStatus || ""} onChange={(e) => patch("form1", { maritalStatus: e.target.value })}>
                  <option value="">اختر</option>
                  {MARITAL_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="الهاتف">
                <input value={draft.form1.phone || ""} onChange={(e) => patch("form1", { phone: e.target.value })} />
              </Field>
              <Field label="العنوان" wide>
                <input value={draft.form1.address || ""} onChange={(e) => patch("form1", { address: e.target.value })} />
              </Field>
              <Field label="المؤهل الدراسي">
                <input value={draft.form1.qualification || ""} onChange={(e) => patch("form1", { qualification: e.target.value })} />
              </Field>
              <Field label="المهنة">
                <input value={draft.form1.jobTitle || ""} onChange={(e) => patch("form1", { jobTitle: e.target.value })} />
              </Field>
              <Field label="تاريخ الالتحاق">
                <input type="date" value={String(draft.form1.hireDate || "").slice(0, 10)} onChange={(e) => patch("form1", { hireDate: e.target.value })} />
              </Field>
              <Field label="تاريخ بدء الاشتراك">
                <input type="date" value={String(draft.form1.subscriptionStartDate || "").slice(0, 10)} onChange={(e) => patch("form1", { subscriptionStartDate: e.target.value, })} />
              </Field>
              <Field label="نوع مدة العمل">
                <select value={draft.form1.employmentType || "PERMANENT"} onChange={(e) => patch("form1", { employmentType: e.target.value })}>
                  {EMPLOYMENT_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="الأجر الأساسي">
                <input value={draft.form1.basicWage ?? ""} onChange={(e) => patch("form1", { basicWage: e.target.value })} />
              </Field>
              <Field label="الأجر المتغير">
                <input value={draft.form1.variableWage ?? ""} onChange={(e) => patch("form1", { variableWage: e.target.value })} />
              </Field>
              <Field label="الأجر الشامل">
                <input value={draft.form1.totalWage ?? ""} onChange={(e) => patch("form1", { totalWage: e.target.value })} />
              </Field>
              <Field label="صاحب العمل أو المفوض" wide>
                <input value={draft.form1.authorizedPersonName || ""} onChange={(e) => patch("form1", { authorizedPersonName: e.target.value })} />
              </Field>
            </fieldset>
          ) : null}

          {tab === "form1" ? (
            <div className="si-checks">
              <label>
                <input
                  type="checkbox"
                  disabled={locked}
                  checked={Boolean(draft.form1.hasPreviousPeriods)}
                  onChange={(e) => patch("form1", { hasPreviousPeriods: e.target.checked })}
                />
                توجد مدد اشتراك سابقة (يُستكمل نموذج 3)
              </label>
              <label>
                <input type="checkbox" disabled={locked} checked={Boolean(draft.form1.insuredSigned)} onChange={(e) => patch("form1", { insuredSigned: e.target.checked })} />
                تم توقيع المؤمن عليه على الأصل
              </label>
              <label>
                <input type="checkbox" disabled={locked} checked={Boolean(draft.form1.employerSigned)} onChange={(e) => patch("form1", { employerSigned: e.target.checked })} />
                تم توقيع صاحب العمل أو المفوض على الأصل
              </label>
              <label>
                <input type="checkbox" disabled={locked} checked={Boolean(draft.form1.stamped)} onChange={(e) => patch("form1", { stamped: e.target.checked })} />
                تم ختم المنشأة
              </label>
            </div>
          ) : null}

          {tab === "form3" ? (
            <div>
              <p className="si-muted">صحيفة البيانات الأساسية. تُستكمل عند وجود مدد سابقة.</p>
              {(draft.form3.periods || []).map((row, index) => (
                <div className="si-period" key={`period-${index}`}>
                  <input disabled={locked} placeholder="جهة العمل" value={row.employerName || ""} onChange={(e) => {
                    const periods = [...draft.form3.periods];
                    periods[index] = { ...row, employerName: e.target.value };
                    patch("form3", { periods });
                  }} />
                  <input disabled={locked} placeholder="رقم المنشأة" value={row.establishmentNumber || ""} onChange={(e) => {
                    const periods = [...draft.form3.periods];
                    periods[index] = { ...row, establishmentNumber: e.target.value };
                    patch("form3", { periods });
                  }} />
                  <input disabled={locked} type="date" value={String(row.from || "").slice(0, 10)} onChange={(e) => {
                    const periods = [...draft.form3.periods];
                    periods[index] = { ...row, from: e.target.value };
                    patch("form3", { periods });
                  }} />
                  <input disabled={locked} type="date" value={String(row.to || "").slice(0, 10)} onChange={(e) => {
                    const periods = [...draft.form3.periods];
                    periods[index] = { ...row, to: e.target.value };
                    patch("form3", { periods });
                  }} />
                  <input disabled={locked} placeholder="الأجر" value={row.wage || ""} onChange={(e) => {
                    const periods = [...draft.form3.periods];
                    periods[index] = { ...row, wage: e.target.value };
                    patch("form3", { periods });
                  }} />
                  <input disabled={locked} placeholder="سبب انتهاء الخدمة" value={row.endReason || ""} onChange={(e) => {
                    const periods = [...draft.form3.periods];
                    periods[index] = { ...row, endReason: e.target.value };
                    patch("form3", { periods });
                  }} />
                  {!locked ? (
                    <button
                      type="button"
                      onClick={() => patch("form3", {
                        periods: draft.form3.periods.filter((_, rowIndex) => rowIndex !== index),
                      })}
                    >
                      حذف المدة
                    </button>
                  ) : null}
                </div>
              ))}
              {!locked ? (
                <button
                  type="button"
                  onClick={() => patch("form3", { periods: [...(draft.form3.periods || []), emptyPeriod()] })}
                >
                  إضافة مدة
                </button>
              ) : null}
            </div>
          ) : null}

          {tab === "form4" ? (
            <div className="si-checks">
              <label>
                <input
                  type="checkbox"
                  disabled={locked}
                  checked={Boolean(draft.form4.hasOtherPension)}
                  onChange={(e) => patch("form4", { hasOtherPension: e.target.checked })}
                />
                المؤمن عليه يستحق معاشاً آخر
              </label>
              <Field label="نوع المعاش الآخر" wide>
                <input disabled={locked} value={draft.form4.otherPensionType || ""} onChange={(e) => patch("form4", { otherPensionType: e.target.value })} />
              </Field>
              <Field label="جهة صرف المعاش" wide>
                <input disabled={locked} value={draft.form4.otherPensionSource || ""} onChange={(e) => patch("form4", { otherPensionSource: e.target.value })} />
              </Field>
              <Field label="تاريخ الاستحقاق">
                <input disabled={locked} type="date" value={String(draft.form4.otherPensionStartDate || "").slice(0, 10)} onChange={(e) => patch("form4", { otherPensionStartDate: e.target.value })} />
              </Field>
              <Field label="إقرار المؤمن عليه" wide>
                <textarea disabled={locked} rows={4} value={draft.form4.declaration || ""} onChange={(e) => patch("form4", { declaration: e.target.value })} />
              </Field>
            </div>
          ) : null}

          {tab === "form5" ? (
            <div className="si-checks">
              {[
                ["oldAge", "oldAgeStart", "تأمين الشيخوخة والعجز والوفاة"],
                ["workInjury", "workInjuryStart", "تأمين إصابات العمل"],
                ["sickness", "sicknessStart", "تأمين المرض"],
                ["unemployment", "unemploymentStart", "تأمين البطالة"],
              ].map(([flag, start, label]) => (
                <div className="si-cover" key={flag}>
                  <label>
                    <input
                      type="checkbox"
                      disabled={locked}
                      checked={Boolean(draft.form5[flag])}
                      onChange={(e) => patch("form5", { [flag]: e.target.checked })}
                    />
                    {label}
                  </label>
                  <input
                    type="date"
                    disabled={locked || !draft.form5[flag]}
                    value={String(draft.form5[start] || "").slice(0, 10)}
                    onChange={(e) => patch("form5", { [start]: e.target.value })}
                  />
                </div>
              ))}
              <Field label="الأجر الأساسي الخاضع">
                <input disabled={locked} value={draft.form5.basicWage ?? ""} onChange={(e) => patch("form5", { basicWage: e.target.value })} />
              </Field>
              <Field label="الأجر المتغير الخاضع">
                <input disabled={locked} value={draft.form5.variableWage ?? ""} onChange={(e) => patch("form5", { variableWage: e.target.value })} />
              </Field>
              <Field label="تاريخ بدء التغطية">
                <input disabled={locked} type="date" value={String(draft.form5.subscriptionStartDate || "").slice(0, 10)} onChange={(e) => patch("form5", { subscriptionStartDate: e.target.value })} />
              </Field>
            </div>
          ) : null}

          {tab === "files" ? (
            <div>
              {ATTACHMENT_KINDS.map((kind) => {
                const files = (draft.attachments || []).filter((file) => file.kind === kind.value);
                const needed =
                  kind.required === true ||
                  (kind.required === "professional" && review.professional);
                return (
                  <div className="si-file" key={kind.value}>
                    <div>
                      <strong>
                        {kind.label}
                        {needed ? " *" : ""}
                      </strong>
                      {kind.hint ? <small>{kind.hint}</small> : null}
                      {files.map((file) => (
                        <small key={file.id}>
                          {file.originalName}{" "}
                          <button type="button" onClick={() => downloadSocialInsuranceAttachment(file.id, file.originalName)}>
                            تنزيل
                          </button>
                          {!locked ? (
                            <button type="button" onClick={() => onDeleteAttachment(file.id)}>
                              حذف
                            </button>
                          ) : null}
                        </small>
                      ))}
                    </div>
                    {!locked ? (
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.target.value = "";
                          if (file) onUpload(kind.value, file);
                        }}
                      />
                    ) : null}
                  </div>
                );
              })}
              <Field label="ملاحظات التسليم للمكتب" wide>
                <textarea
                  disabled={readOnly}
                  rows={3}
                  value={draft.submissionNotes || ""}
                  onChange={(e) => setDraft((prev) => ({ ...prev, submissionNotes: e.target.value }))}
                />
              </Field>
              <Field label="رقم التأمين بعد التسجيل">
                <input
                  disabled={readOnly}
                  value={draft.insuranceNumber || ""}
                  onChange={(e) => setDraft((prev) => ({ ...prev, insuranceNumber: e.target.value }))}
                />
              </Field>
              {!readOnly && draft.status === "SUBMITTED" ? (
                <button type="button" className="is-primary" disabled={busy} onClick={() => persist("REGISTERED")}>
                  تم التسجيل ورقم التأمين
                </button>
              ) : null}
              {!readOnly && draft.status === "REGISTERED" ? (
                <button type="button" disabled={busy} onClick={() => persist("REGISTERED")}>
                  تحديث رقم التأمين
                </button>
              ) : null}
            </div>
          ) : null}

          {review.missing.length ? (
            <ul className="si-missing">
              {review.missing.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className="si-ok">الملف مستوفٍ للتسليم إلى مكتب التأمينات.</p>
          )}
          <SocialInsurancePrint draft={draft} employee={selected.employee} />
        </section>
      ) : (
        <>
          <div className="si-stats">
            <div className="si-stat"><strong>{summary.total}</strong><span>موظف</span></div>
            <div className="si-stat"><strong>{summary.overdue}</strong><span>متأخر عن الأسبوعين</span></div>
            <div className="si-stat"><strong>{summary.soon}</strong><span>آخر 3 أيام</span></div>
            <div className="si-stat"><strong>{summary.submitted}</strong><span>تم تسليمه</span></div>
          </div>
          <div className="si-filters">
            {[
              ["open", "بانتظار التسليم"],
              ["overdue", "متأخر"],
              ["soon", "قرب انتهاء المهلة"],
              ["submitted", "تم التسليم"],
              ["all", "الكل"],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`si-chip${filter === id ? " is-on" : ""}`}
                onClick={() => setFilter(id)}
              >
                {label}
              </button>
            ))}
            <input
              className="si-search"
              placeholder="بحث بالاسم أو الكود"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="si-table-card">
            <table className="si-table">
              <thead>
                <tr>
                  <th>الموظف</th>
                  <th>الالتحاق</th>
                  <th>المهلة</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4}>جارٍ التحميل...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={4}>لا توجد نتائج</td></tr>
                ) : (
                  rows.map((item) => (
                    <tr key={item.employee.id}>
                      <td>
                        <button type="button" className="linkish" onClick={() => openEmployee(item)}>
                          {item.employee.fullName}
                        </button>
                        <div className="si-muted">
                          {item.employee.employeeCode} — {item.employee.jobTitle}
                        </div>
                      </td>
                      <td>{item.employee.hireDate || "—"}</td>
                      <td className={`si-tone-${item.deadline?.tone || "ok"}`}>
                        {deadlineLabel(item.deadline)}
                      </td>
                      <td>{INSURANCE_STATUSES[item.insuranceCase?.status] || "لم يبدأ"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
